(() => {
  'use strict';
  if (window.GrundschulFuxCartGuard) return;

  const root = window.Shopify?.routes?.root || '/';
  const url = (path) => `${root}${path}`;
  let busy = false;
  let cartCheckPending = false;
  let notice;

  function notify(message) {
    clearTimeout(notify.timer);
    if (notice) notice.remove();
    const host = document.createElement('div');
    host.id = 'gfx-cart-notice';
    notice = host;
    const styles = {
      all: 'initial', position: 'fixed',
      top: 'calc(16px + env(safe-area-inset-top, 0px))', bottom: 'auto',
      left: '12px', right: '12px', width: 'auto', height: 'auto',
      'max-width': '520px', margin: '0 auto', padding: '0', border: '0',
      background: 'transparent', overflow: 'visible',
      'z-index': '2147483647', 'pointer-events': 'none'
    };
    for (const [property, value] of Object.entries(styles)) {
      host.style.setProperty(property, value, 'important');
    }
    const shadow = host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = '.message{box-sizing:border-box;padding:16px 18px;border-radius:10px;background:#222;color:#fff;font:600 16px/1.5 system-ui,sans-serif;text-align:left;overflow-wrap:anywhere;border-left:5px solid #f2c14e;box-shadow:0 4px 20px rgba(0,0,0,.25)}';
    const panel = document.createElement('div');
    panel.className = 'message';
    panel.setAttribute('role', 'alert');
    panel.setAttribute('aria-atomic', 'true');
    panel.textContent = message;
    shadow.append(style, panel);
    const supportsPopover = typeof host.showPopover === 'function';
    if (supportsPopover) host.setAttribute('popover', 'manual');
    document.documentElement.appendChild(host);
    try {
      if (supportsPopover) host.showPopover();
      else host.style.setProperty('display', 'block', 'important');
    } catch {
      host.removeAttribute('popover');
      host.style.setProperty('display', 'block', 'important');
    }
    notify.timer = setTimeout(() => {
      host.remove();
      if (notice === host) notice = null;
    }, 3500);
  }

  async function requestJson(path, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url(path), {
        ...options, credentials: 'same-origin', cache: 'no-store',
        signal: controller.signal
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        throw new Error(typeof data.description === 'string'
          ? data.description : 'Der Warenkorb konnte nicht aktualisiert werden.');
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Die Verbindung dauert zu lange. Bitte prüfe den Warenkorb vor einem erneuten Versuch.');
      }
      throw error;
    } finally {
      clearTimeout(timer);
    }
  }

  // V3: maximum one item per variant, not per product.
  async function run(selection, operation) {
    const variantId = String(selection.variantId || '');
    if (!/^\d+$/.test(variantId)) throw new Error('Die Variante konnte nicht zugeordnet werden. Bitte lade die Seite neu.');
    if (busy) throw new Error('Der Warenkorb wird gerade geprüft. Bitte einen Moment warten.');
    busy = true;
    try {
      const cart = await requestJson('cart.js');
      if (!Array.isArray(cart.items)) throw new Error('Der Warenkorb konnte nicht geprüft werden.');
      if (cart.items.some(item => String(item.variant_id || item.id) === variantId)) {
        throw new Error('Dieses Produkt liegt bereits im Warenkorb.');
      }
      return await operation();
    } finally { busy = false; }
  }

  async function normalize() {
    if (busy) throw new Error('Der Warenkorb wird gerade aktualisiert.');
    busy = true;
    try {
      const cart = await requestJson('cart.js');
      if (!Array.isArray(cart.items)) throw new Error('Der Warenkorb konnte nicht geprüft werden.');
      const seen = new Set();
      const updates = {};
      for (const item of cart.items) {
        const id = String(item.variant_id || item.id || '');
        if (!id || !item.key) throw new Error('Eine Warenkorbposition konnte nicht zugeordnet werden.');
        const quantity = seen.has(id) ? 0 : 1;
        seen.add(id);
        if (item.quantity !== quantity) updates[item.key] = quantity;
      }
      if (!Object.keys(updates).length) return false;
      const result = await requestJson('cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!Array.isArray(result.items)) throw new Error('Der aktualisierte Warenkorb konnte nicht geprüft werden.');
      const verified = new Set();
      for (const item of result.items) {
        const id = String(item.variant_id || item.id);
        if (item.quantity !== 1 || verified.has(id)) throw new Error('Die Menge konnte nicht auf 1 begrenzt werden. Bitte entferne doppelte Positionen.');
        verified.add(id);
      }
      return true;
    } finally { busy = false; }
  }

  window.GrundschulFuxCartGuard = { run, normalize, notify, version: '3.0-popup-variant' };
  let searchPending = false;
  let modalOpen = false;

  function productContext(button) {
    const link = button.closest('a.snize-view-link');
    const productUrl = new URL(link?.getAttribute('href') || '', location.origin);
    const match = productUrl.pathname.match(/\/products\/([^/]+)\/?$/);
    if (!link || productUrl.origin !== location.origin || !match) {
      throw new Error('Bitte öffne das Produkt, um es in den Warenkorb zu legen.');
    }
    return decodeURIComponent(match[1]);
  }

  function chooseVariant(product, opener) {
    if (typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') {
      throw new Error('Bitte öffne die Produktseite zur Variantenauswahl. Dein Browser unterstützt dieses Auswahlfenster nicht.');
    }
    return new Promise((resolve, reject) => {
      const dialog = document.createElement('dialog');
      dialog.id = 'gfx-variant-dialog';
      dialog.setAttribute('aria-labelledby', 'gfx-variant-title');
      dialog.setAttribute('aria-describedby', 'gfx-variant-help');
      const style = document.createElement('style');
      style.textContent = `
        #gfx-variant-dialog{box-sizing:border-box;width:calc(100% - 32px);max-width:460px;max-height:85dvh;overflow:auto;margin:auto;padding:24px;border:0;border-radius:16px;background:#fff;color:#222;box-shadow:0 12px 60px #0004;font:16px/1.5 system-ui,sans-serif}
        #gfx-variant-dialog::backdrop{background:rgba(0,0,0,.65)}
        #gfx-variant-dialog .gfx-close{display:block;margin:0 0 12px auto;min-height:44px;min-width:44px;border:1px solid #bbb;border-radius:8px;background:#fff;color:#222;font:inherit;cursor:pointer}
        #gfx-variant-dialog h2{margin:0 0 8px;font:700 21px/1.3 system-ui,sans-serif;color:#222;overflow-wrap:anywhere}
        #gfx-variant-dialog p{margin:0 0 18px;font:inherit;color:#444}
        #gfx-variant-dialog .gfx-options{display:grid;gap:12px}
        #gfx-variant-dialog .gfx-option{box-sizing:border-box;width:100%;min-height:64px;padding:14px 16px;text-align:left;border:2px solid #333;border-radius:10px;background:#fff;color:#222;font:600 16px/1.4 system-ui,sans-serif;cursor:pointer;white-space:normal;overflow-wrap:anywhere}
        #gfx-variant-dialog .gfx-option:hover:not(:disabled){background:#fff5d8;border-color:#8a6500}
        #gfx-variant-dialog button:focus-visible{outline:3px solid #8a6500;outline-offset:3px}
        #gfx-variant-dialog button:disabled{opacity:.55;cursor:not-allowed}
        #gfx-variant-dialog .gfx-price{display:block;margin-top:4px;font-weight:400}
      `;
      const close = document.createElement('button');
      close.type = 'button'; close.className = 'gfx-close'; close.textContent = 'Schließen ×';
      const title = document.createElement('h2');
      title.id = 'gfx-variant-title'; title.textContent = product.title || 'Variante wählen';
      const help = document.createElement('p');
      help.id = 'gfx-variant-help'; help.textContent = 'Wähle deine Ausführung. Ein Klick legt sie in den Warenkorb.';
      const options = document.createElement('div'); options.className = 'gfx-options';
      const price = new Intl.NumberFormat('de-DE', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' });
      let settled = false;
      const overflow = document.body.style.overflow;
      const finish = (variant) => {
        if (settled) return;
        settled = true;
        if (dialog.open) dialog.close();
        dialog.remove(); style.remove();
        document.body.style.overflow = overflow;
        modalOpen = false;
        if (opener.isConnected) opener.focus({ preventScroll: true });
        resolve(variant);
      };
      for (const variant of product.variants) {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'gfx-option'; button.disabled = !variant.available;
        const name = document.createElement('span'); name.textContent = variant.title;
        const amount = document.createElement('span'); amount.className = 'gfx-price';
        amount.textContent = `${price.format(Number(variant.price) / 100)}${variant.available ? '' : ' · Nicht verfügbar'}`;
        button.append(name, amount);
        button.addEventListener('click', () => finish(variant));
        options.appendChild(button);
      }
      close.addEventListener('click', () => finish(null));
      dialog.addEventListener('cancel', event => { event.preventDefault(); finish(null); });
      dialog.addEventListener('close', () => finish(null));
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) finish(null);
      });
      dialog.append(close, title, help, options);
      document.head.appendChild(style); document.body.appendChild(dialog);
      try {
        modalOpen = true; document.body.style.overflow = 'hidden';
        dialog.showModal();
        (options.querySelector('button:not(:disabled)') || close).focus();
      } catch (error) {
        settled = true;
        dialog.remove(); style.remove();
        document.body.style.overflow = overflow; modalOpen = false;
        reject(error);
      }
    });
  }

  async function addSearchVariant(variant, button) {
    if (!variant.available) throw new Error('Diese Ausführung ist derzeit nicht verfügbar.');
    await run({ variantId: variant.id }, async () => {
      const cartUi = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const ready = cartUi && typeof cartUi.getSectionsToRender === 'function' && typeof cartUi.renderContents === 'function';
      const sections = ready ? cartUi.getSectionsToRender().map(section => section.id) : ['cart-icon-bubble'];
      const body = { id: variant.id, quantity: 1, sections, sections_url: location.pathname };
      if (ready && typeof cartUi.setActiveElement === 'function') cartUi.setActiveElement(button);
      const response = await requestJson('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      let refreshed = false;
      try {
        if (ready && sections.length && sections.every(id => typeof response.sections?.[id] === 'string')) {
          cartUi.renderContents(response); refreshed = true;
        } else {
          const bubble = document.getElementById('cart-icon-bubble');
          const html = response.sections?.['cart-icon-bubble'];
          if (bubble && typeof html === 'string') {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const replacement = parsed.getElementById('cart-icon-bubble');
            if (replacement) { bubble.innerHTML = replacement.innerHTML; refreshed = true; }
          }
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'gfx-searchanise', productVariantId: String(variant.id), cartData: response
          })).catch(console.warn);
        }
      } catch (error) { console.warn('GrundschulFux: Anzeigeaktualisierung fehlgeschlagen.', error); }
      if (!refreshed) {
        console.warn(
          'GrundschulFux: Produkt hinzugefügt, Warenkorbanzeige nicht aktualisiert.'
        );
      }
    });
  }

  // Capture the existing Searchanise button, without global XHR interception.
  window.addEventListener('click', async event => {
    const target = event.target instanceof Element ? event.target : null;
    if (cartCheckPending && target?.closest('[name="checkout"]')) {
      event.preventDefault(); event.stopImmediatePropagation();
      notify('Der Warenkorb wird geprüft. Bei einer Fehlermeldung bitte die Seite neu laden.'); return;
    }
    const button = target?.closest('.snize-add-to-cart-button');
    if (!button) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (searchPending || modalOpen || button.disabled) return;
    searchPending = true;
    // Keep the opener focusable while the modal is open, but mark it busy.
    button.setAttribute('aria-busy', 'true');
    try {
      const handle = productContext(button);
      const product = await requestJson(`products/${encodeURIComponent(handle)}.js`);
      if (!Array.isArray(product.variants) || !product.variants.length) throw new Error('Das Produkt konnte nicht geladen werden.');
      const variant = product.variants.length === 1 ? product.variants[0] : await chooseVariant(product, button);
      if (variant) await addSearchVariant(variant, button);
    } catch (error) {
      notify(error.message || 'Der Warenkorb konnte nicht geprüft werden. Bitte versuche es erneut.');
    } finally {
      searchPending = false; button.removeAttribute('aria-busy');
    }
  }, true);

  window.addEventListener('submit', event => {
    if (!cartCheckPending || event.submitter?.name !== 'checkout') return;
    event.preventDefault(); event.stopImmediatePropagation();
    notify('Der Warenkorb wird geprüft. Bei einer Fehlermeldung bitte die Seite neu laden.');
  }, true);

  window.addEventListener('pageshow', async () => {
    const cartPath = new URL(window.routes?.cart_url || url('cart'), location.origin).pathname;
    if (location.pathname.replace(/\/$/, '') !== cartPath.replace(/\/$/, '')) return;
    cartCheckPending = true;
    try {
      if (await normalize()) { location.reload(); return; }
      cartCheckPending = false;
    } catch (error) { notify(error.message || 'Der Warenkorb konnte nicht geprüft werden.'); }
  });
})();
